import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  CurrencyApiDownload,
  CurrencyDto,
  CurrencyExchangeRateDto,
  CurrencyExchangeRateListQuery,
  CurrencyExchangeRateListResult,
  CurrencyExchangeRateRequest,
  CurrencyFilterSelects,
  CurrencyFormSelects,
  CurrencyListQuery,
  CurrencyListResult,
  CurrencyRequest,
  CurrencySelectOption,
  CurrencyStatusDto,
} from './currencies-api.types';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const CURRENCIES_PATH = '/catalogs/currencies';

@Injectable({ providedIn: 'root' })
export class CurrenciesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${CURRENCIES_PATH}`;

  list(query: CurrencyListQuery = {}): Observable<CurrencyListResult> {
    return this.safeListRequest(
      this.http.get<unknown>(this.baseUrl, {
        params: currencyListParams(query),
      }),
      unwrapCurrencyList,
    );
  }

  formSelects(): Promise<CurrencyFormSelects> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/form-selects`),
      unwrapFormSelects,
    );
  }

  filterSelects(): Promise<CurrencyFilterSelects> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/filter-selects`),
      unwrapFilterSelects,
    );
  }

  getById(uuid: string): Promise<CurrencyDto> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}`),
      unwrapCurrency,
    );
  }

  create(payload: CurrencyRequest): Promise<CurrencyDto> {
    return this.request(
      this.http.post<unknown>(this.baseUrl, payload),
      unwrapCurrency,
    );
  }

  update(uuid: string, payload: CurrencyRequest): Promise<CurrencyDto> {
    return this.request(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapCurrency,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  exportCurrencies(query: CurrencyListQuery = {}): Promise<CurrencyApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      currencyListParams(query, false),
      'divisas.xlsx',
    );
  }

  listExchangeRates(
    currencyUuid: string,
    query: CurrencyExchangeRateListQuery = {},
  ): Observable<CurrencyExchangeRateListResult> {
    return this.safeListRequest(
      this.http.get<unknown>(this.ratesUrl(currencyUuid), {
        params: rateListParams(query),
      }),
      unwrapRateList,
    );
  }

  getExchangeRate(
    currencyUuid: string,
    rateUuid: string,
  ): Promise<CurrencyExchangeRateDto> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.ratesUrl(currencyUuid)}/${rateUuid}`),
      unwrapRate,
    );
  }

  createExchangeRate(
    currencyUuid: string,
    payload: CurrencyExchangeRateRequest,
  ): Promise<CurrencyExchangeRateDto> {
    return this.request(
      this.http.post<unknown>(this.ratesUrl(currencyUuid), payload),
      unwrapRate,
    );
  }

  updateExchangeRate(
    currencyUuid: string,
    rateUuid: string,
    payload: CurrencyExchangeRateRequest,
  ): Promise<CurrencyExchangeRateDto> {
    return this.request(
      this.http.put<unknown>(`${this.ratesUrl(currencyUuid)}/${rateUuid}`, payload),
      unwrapRate,
    );
  }

  removeExchangeRate(currencyUuid: string, rateUuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.ratesUrl(currencyUuid)}/${rateUuid}`),
      () => undefined,
    );
  }

  exportExchangeRates(
    currencyUuid: string,
    query: CurrencyExchangeRateListQuery = {},
  ): Promise<CurrencyApiDownload> {
    return this.download(
      `${this.ratesUrl(currencyUuid)}/excel`,
      rateListParams(query, false),
      'tipos-de-cambio.xlsx',
    );
  }

  private ratesUrl(currencyUuid: string): string {
    return `${this.baseUrl}/${currencyUuid}/exchange-rates`;
  }

  private safeListRequest<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Observable<T> {
    return source.pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(project),
      catchError(error => throwError(() => mapCurrenciesError(error))),
    );
  }

  private safeRequest<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    return this.request(
      source.pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapCurrenciesError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<CurrencyApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, {
            params,
            observe: 'response',
            responseType: 'blob',
          })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapCurrenciesError(error);
    }
  }
}

function currencyListParams(
  query: CurrencyListQuery,
  includePagination = true,
): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.code) params = params.set('filters[code]', query.code);
  if (query.symbol) params = params.set('filters[symbol]', query.symbol);
  if (query.name) params = params.set('filters[name]', query.name);
  if (query.type) params = params.set('filters[type]', query.type);
  if (query.statusUuids?.length) {
    params = params.set('select[status]', query.statusUuids.join(','));
  }
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function rateListParams(
  query: CurrencyExchangeRateListQuery,
  includePagination = true,
): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.date) params = params.set('filters[date]', query.date);
  if (query.commercialRate) {
    params = params.set('filters[commercialRate]', query.commercialRate);
  }
  if (query.fiscalRate) params = params.set('filters[fiscalRate]', query.fiscalRate);
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) {
      return timer(300);
    }
  }
  return throwError(() => error);
}

function mapCurrenciesError(error: unknown): SettingsApiError {
  const mapped = mapSettingsError(error);
  if (!(error instanceof HttpErrorResponse)) return mapped;
  const body = asRecord(error.error);
  if (!body) return mapped;
  const business = stringField(body, ['error']);
  const details = detailMessages(body['details']).filter(item => item !== business);
  if (business && details.length) {
    mapped.message = [business.replace(/\.$/, ''), ...details].join('. ');
  } else if (business) {
    mapped.message = business;
  } else if (details.length) {
    mapped.message = details.join('. ');
  }
  return mapped;
}

function unwrapCurrencyList(body: unknown): CurrencyListResult {
  return unwrapPagedList(body, normalizeCurrency);
}

function unwrapRateList(body: unknown): CurrencyExchangeRateListResult {
  return unwrapPagedList(body, normalizeRate);
}

function unwrapPagedList<T>(
  body: unknown,
  normalize: (raw: unknown) => T,
): { items: T[]; total: number } {
  const data = unwrapData(body);
  if (Array.isArray(data)) {
    return { items: data.map(normalize), total: data.length };
  }
  const page = asRecord(data);
  if (!page) throw new SettingsApiError('unexpected', 0);
  const items = firstArray(page, ['data', 'content', 'items']).map(normalize);
  const pagination = asRecord(page['pagination']);
  const total =
    numberField(pagination, ['totalRecords']) ??
    numberField(page, ['totalElements', 'total']) ??
    items.length;
  return { items, total };
}

function unwrapCurrency(body: unknown): CurrencyDto {
  return normalizeCurrency(requiredData(body));
}

function unwrapRate(body: unknown): CurrencyExchangeRateDto {
  return normalizeRate(requiredData(body));
}

function unwrapFormSelects(body: unknown): CurrencyFormSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);
  return { types: selectArray(data, ['types']) };
}

function unwrapFilterSelects(body: unknown): CurrencyFilterSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);
  return { statuses: selectArray(data, ['status', 'statuses']) };
}

function normalizeCurrency(raw: unknown): CurrencyDto {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const code = stringField(record, ['code']);
  const symbol = stringField(record, ['symbol']);
  const name = stringField(record, ['name']);
  const type = record ? normalizeSelect(record['type']) : null;
  const status = record ? normalizeStatus(record['status']) : null;
  if (!record || !uuid || !code || !symbol || !name || !type || !status) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    uuid,
    code,
    symbol,
    name,
    type,
    status,
    createdAt: optionalString(record['createdAt']),
    updatedAt: optionalString(record['updatedAt']),
  };
}

function normalizeRate(raw: unknown): CurrencyExchangeRateDto {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const date = stringField(record, ['date']);
  const commercialRate = numberField(record, ['commercialRate']);
  const fiscalRate = numberField(record, ['fiscalRate']);
  if (!record || !uuid || !date || commercialRate === null || fiscalRate === null) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    uuid,
    currencyUuid: stringField(record, ['currencyUuid']) ?? '',
    date,
    commercialRate,
    fiscalRate,
    createdAt: optionalString(record['createdAt']),
    updatedAt: optionalString(record['updatedAt']),
  };
}

function normalizeSelect(raw: unknown): CurrencySelectOption | null {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const name = stringField(record, ['name']);
  return uuid && name ? { uuid, name } : null;
}

function normalizeStatus(raw: unknown): CurrencyStatusDto | null {
  const record = asRecord(raw);
  const name = stringField(record, ['name']);
  const value = stringField(record, ['value', 'uuid']);
  const tone = stringField(record, ['tone']);
  return name && value && tone ? { name, value, tone } : null;
}

function selectArray(
  record: Record<string, unknown>,
  keys: string[],
): CurrencySelectOption[] {
  return firstArray(record, keys)
    .map(normalizeSelect)
    .filter((item): item is CurrencySelectOption => item !== null);
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): CurrencyApiDownload {
  if (!response.body) throw new SettingsApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function detailMessages(details: unknown): string[] {
  const record = asRecord(details);
  if (!record) return [];
  const messages: string[] = [];
  for (const value of Object.values(record)) {
    if (typeof value === 'string' && value.trim()) messages.push(value.trim());
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) messages.push(item.trim());
    }
  }
  return messages;
}

function requiredData(body: unknown): unknown {
  const data = unwrapData(body);
  if (data === undefined || data === null) {
    throw new SettingsApiError('unexpected', 0);
  }
  return data;
}

function unwrapData(body: unknown): unknown {
  const record = asRecord(body);
  if (
    record &&
    'data' in record &&
    (Object.keys(record).length === 1 ||
      'message' in record ||
      'status' in record ||
      'error' in record)
  ) {
    return record['data'];
  }
  if (
    record &&
    !('data' in record) &&
    ('message' in record || 'status' in record || 'error' in record)
  ) {
    return undefined;
  }
  return body;
}

function firstArray(record: Record<string, unknown>, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringField(
  record: Record<string, unknown> | null,
  keys: string[],
): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function numberField(
  record: Record<string, unknown> | null,
  keys: string[],
): number | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
